/**
 * @Author: Nemo
 * @Description: 校验核心 - 执行校验逻辑
 * @Date: 2025-05-07
 * @FilePath: /rhcn-checkout-web/src/plugins/validator/core/validator.js
 */
import { getRule } from './registry';

/**
 * 创建校验器实例
 * @param {object} i18n 国际化对象 (可选)
 * @param {object} options 配置选项 (可选)
 */
export function createValidator(i18n = null, options = {}) {
  // 默认i18n处理
  const translator = i18n || {
    t: (key, params = {}) => {
      // 简单替换参数
      let text = key;
      Object.entries(params).forEach(([key, value]) => {
        text = text.replace(`{${key}}`, value);
      });
      return text;
    },
  };

  /**
   * 校验单个值
   * @param {any} value 要校验的值
   * @param {array|object} rules 规则配置
   */
  function validate(value, rules) {
    if (!rules || ((!Array.isArray(rules)) && (typeof rules !== 'object'))) {
      return { valid: true };
    }

    // 转换规则为统一格式
    const ruleConfigs = normalizeRules(rules);

    // 校验每个规则
    for (const config of ruleConfigs) {
      const result = validateRule(value, config);

      if (!result.valid) {
        return result;
      }
    }

    return { valid: true };
  }

  /**
   * 校验整个表单
   * @param {object} formData 表单数据
   * @param {object} schema 校验规则架构
   */
  function validateAll(formData, schema) {
    const errors = {};
    let valid = true;

    // 校验每个字段
    for (const field in schema) {
      if (!Object.prototype.hasOwnProperty.call(schema, field)) continue;

      const value = formData[field];
      const result = validate(value, schema[field]);

      if (!result.valid) {
        errors[field] = result.error;
        valid = false;
      }
    }

    return {
      valid,
      errors,
    };
  }

  /**
   * 异步校验整个表单
   * @param {object} formData 表单数据
   * @param {object} schema 校验规则架构
   */
  async function validateAllAsync(formData, schema) {
    const errors = {};
    let valid = true;

    // 收集所有校验任务
    const validationTasks = [];

    for (const field in schema) {
      if (!Object.prototype.hasOwnProperty.call(schema, field)) continue;

      validationTasks.push(
        validateFieldAsync(field, formData[field], schema[field]),
      );
    }

    // 等待所有校验完成
    const results = await Promise.all(validationTasks);

    // 处理结果
    results.forEach(({ field, result }) => {
      if (!result.valid) {
        errors[field] = result.error;
        valid = false;
      }
    });

    return {
      valid,
      errors,
    };
  }

  /**
   * 异步校验单个字段
   * @private
   */
  async function validateFieldAsync(field, value, rules) {
    const result = validate(value, rules);
    return { field, result };
  }

  /**
   * 校验单个规则
   * @private
   */
  function validateRule(value, ruleConfig) {
    // 解析规则配置
    const { name, params = [] } = ruleConfig;

    try {
      // 获取规则实现
      const rule = getRule(name);

      // 执行校验
      const valid = rule.validate(value, ...params);

      if (!valid) {
        // 生成错误消息
        const message = typeof rule.message === 'function'
          ? rule.message(translator, { value, ...paramsToObject(params) })
          : rule.message;

        return {
          valid: false,
          rule: name,
          error: message,
        };
      }

      return { valid: true };
    } catch (error) {
      console.error(`校验规则 "${name}" 执行出错:`, error);
      return {
        valid: false,
        error: `校验规则 "${name}" 执行失败`,
      };
    }
  }

  /**
   * 规则格式标准化
   * @private
   */
  function normalizeRules(rules) {
    if (Array.isArray(rules)) {
      return rules.map(rule => {
        if (typeof rule === 'string') {
          return { name: rule, params: [] };
        } else if (Array.isArray(rule)) {
          const [name, ...params] = rule;
          return { name, params };
        }
        return rule;
      });
    }

    return [rules];
  }

  /**
   * 转换参数数组为对象 (用于错误消息)
   * @private
   */
  function paramsToObject(params) {
    const paramNames = ['param1', 'param2', 'param3', 'param4'];
    return params.reduce((obj, value, index) => {
      obj[paramNames[index] || `param${index + 1}`] = value;
      return obj;
    }, {});
  }

  // 返回校验器实例
  return {
    validate,
    validateAll,
    validateAllAsync,
  };
}
